"use client"

import { useEffect, useState } from "react"
import { useRouter } from "next/navigation"
import { useLocale } from "next-intl"
import { useTranslations } from "next-intl"
import { useAuthStore } from "@/lib/auth-store"
import { fetchSeenState, markSeen } from "@/lib/client/seen-state"
import { useModalSlot } from "@/lib/client/modal-coordinator"
import { setLocale } from "@/i18n/set-locale-action"
import { LOCALES, LOCALE_LABELS, type Locale } from "@/i18n/locales"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Globe, Check } from "@/lib/icons"

// Backup/manual onboarding language picker — NOT wired into the normal
// first-run flow (see app/layout.tsx's ONBOARDING_LANGUAGE_MODAL_ENABLED,
// off by default). Locale is already auto-detected from Accept-Language by
// the proxy (lib/supabase/proxy.ts) on first visit with no popup, and
// correctable any time via the Settings-sheet language row (guest-accessible)
// or the profile menu (logged-in). This component exists as a fallback we
// can flip on if that silent auto-detect ever needs a visible, explicit
// first-run confirmation instead — e.g. detection proves unreliable for
// some visitor segment.
//
// If you do enable it: it has no delay/ordering guard against
// AccountIntentPrompt, which also fires as-soon-as-unseen with no artificial
// delay. A fresh signup could see both stack in the same session. Founder's
// promo modal solves this exact problem by deferring to the user's second
// calendar day (see founder-promo-modal.tsx) — copy that pattern here too
// before enabling, or the "too many popups" problem this was built to avoid
// comes right back.
const SEEN_KEY = "modal:onboarding_language"
const LOCAL_SEEN_KEY = "goonru_onboarding_language_seen"

export function OnboardingLanguageModal() {
  const t = useTranslations("onboardingLanguageModal")
  const router = useRouter()
  const locale = useLocale() as Locale
  const { user, profile, isLoading } = useAuthStore()
  const [open, setOpen] = useState(false)
  const [switching, setSwitching] = useState<Locale | null>(null)
  const canShow = useModalSlot("onboardingLanguage", open)

  useEffect(() => {
    if (isLoading) return
    let cancelled = false

    async function check() {
      if (user && profile) {
        const { keys } = await fetchSeenState()
        if (!cancelled && !keys.has(SEEN_KEY)) setOpen(true)
      } else if (!user) {
        try {
          if (!localStorage.getItem(LOCAL_SEEN_KEY)) setOpen(true)
        } catch {}
      }
    }
    void check()
    return () => { cancelled = true }
  }, [user, profile, isLoading])

  function dismiss() {
    if (user) {
      void markSeen(SEEN_KEY)
    } else {
      try { localStorage.setItem(LOCAL_SEEN_KEY, "true") } catch {}
    }
    setOpen(false)
  }

  async function choose(next: Locale) {
    setSwitching(next)
    await setLocale(next)
    dismiss()
    router.refresh()
  }

  return (
    <Dialog open={open && canShow} onOpenChange={(o) => { if (!o) dismiss() }}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            {t("chooseYourLanguage")}
          </DialogTitle>
          <DialogDescription>{t("youCanAlwaysChangeThisLaterInSettings")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-2 pt-2">
          {LOCALES.map((l) => (
            <Button
              key={l}
              variant={l === locale ? "default" : "outline"}
              className="justify-between h-auto py-3"
              disabled={switching !== null}
              onClick={() => choose(l)}
            >
              {LOCALE_LABELS[l]}
              {l === locale && <Check className="h-4 w-4 shrink-0" />}
            </Button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}
